import type { LineItemUnit } from "@/apis/compare/types";
import {
  getForeignCurrencyUnitLabel,
  getUnitLabel,
} from "@/utils/ixd-editor/unit-conversion";

interface WorkbookTableUnits {
  /** 원화 단위 지수. null 이면 원화 단위 선언이 없다. */
  unit: number | null;
  foreignUnits?: LineItemUnit[];
}

interface IxdTableUnits {
  /** 서버가 준 원화 단위 라벨 그대로. null 이면 선언이 없다. */
  unitText: string | null;
  /** 표에 그려지는 라인아이템으로 좁힌 외화 단위 맵 */
  foreignUnits: Record<string, LineItemUnit>;
}

const joinLabels = (labels: Iterable<string>) => [...labels].join(", ");

/** 워크북 원화 지수 → 배율. 0/3/6 밖은 화면이 아는 단위가 아니라 null(모름). */
const workbookKrwDivisor = (unit: number | null): number | null =>
  unit === 0 ? 1 : unit === 3 ? 1_000 : unit === 6 ? 1_000_000 : null;

const IXD_KRW_DIVISOR_BY_LABEL: Record<string, number> = {
  원: 1,
  천원: 1_000,
  백만원: 1_000_000,
};

/**
 * IXD 원화 라벨 → 배율. 공백·괄호·"단위:" 접두는 걷어낸다. 모르는 라벨("억원" 등)은
 * null(모름) — getUnitDivisor 처럼 1로 접으면 "원"과 같다고 잘못 판정한다.
 */
const ixdKrwDivisor = (unitText: string | null): number | null => {
  if (!unitText) return null;
  const normalized = unitText.replace(/[\s()]|단위\s*:?/g, "");
  return IXD_KRW_DIVISOR_BY_LABEL[normalized] ?? null;
};

/**
 * 같은 표의 DSD 워크북과 IXD 단위가 다른 축을 사유 문구로 뽑는다. 다르지 않으면 빈 배열.
 *
 * - 원화: 양쪽 배율을 같은 기준으로 맞춰 비교한다. 선언이 없거나 화면이 모르는
 *   단위(워크북 지수 0/3/6 밖, IXD "억원" 등)는 "다르다"가 아니라 "모른다"라 건너뛴다.
 * - 외화: 양쪽에 모두 있는 통화만, 배율 집합이 다를 때. 한쪽에만 있는 통화는
 *   표 구성 차이라 단위 문제로 보지 않는다.
 */
export const getWorkbookIxdUnitMismatches = (
  workbookTables: readonly WorkbookTableUnits[],
  ixd: IxdTableUnits,
): string[] => {
  const lines: string[] = [];

  const workbookKrwDivisors = new Set(
    workbookTables.flatMap(({ unit }) => {
      const divisor = workbookKrwDivisor(unit);
      return divisor === null ? [] : [divisor];
    }),
  );
  const ixdDivisor = ixdKrwDivisor(ixd.unitText);
  if (
    ixdDivisor !== null &&
    [...workbookKrwDivisors].some((divisor) => divisor !== ixdDivisor)
  ) {
    lines.push(
      `원화 단위가 다릅니다 — DSD 워크북: ${joinLabels([...workbookKrwDivisors].map(getUnitLabel))}, IXD: ${getUnitLabel(ixdDivisor)}`,
    );
  }

  const exponentsByCurrency = (units: Iterable<LineItemUnit>) => {
    const map = new Map<string, Set<number>>();
    for (const { currency, unit } of units) {
      map.set(currency, (map.get(currency) ?? new Set()).add(unit));
    }
    return map;
  };
  const workbookForeign = exponentsByCurrency(
    workbookTables.flatMap(({ foreignUnits }) => foreignUnits ?? []),
  );
  const ixdForeign = exponentsByCurrency(Object.values(ixd.foreignUnits));
  for (const [currency, workbookExponents] of workbookForeign) {
    const ixdExponents = ixdForeign.get(currency);
    if (!ixdExponents) continue;
    const isSame =
      workbookExponents.size === ixdExponents.size &&
      [...workbookExponents].every((unit) => ixdExponents.has(unit));
    if (isSame) continue;
    const label = (exponents: Set<number>) =>
      joinLabels(
        [...exponents]
          .sort((a, b) => a - b)
          .map((unit) => getForeignCurrencyUnitLabel({ currency, unit })),
      );
    lines.push(
      `${currency} 단위가 다릅니다 — DSD 워크북: ${label(workbookExponents)}, IXD: ${label(ixdExponents)}`,
    );
  }

  return lines;
};
